import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { formatDateTimeCT } from '../../../utils/dateUtils';
import { QuillStyles } from '../quillConfig';
import PostGrid from './PostGrid';
import NewsletterSignup from '../NewsletterSignup';

const PostDetail = ({ post, loading, error }) => {
  const [previousPosts, setPreviousPosts] = useState([]);

  // Fetch previous posts when current post is loaded
  useEffect(() => {
    if (post && post.id) {
      fetchPreviousPosts(post.id);
    }
  }, [post]);

  const fetchPreviousPosts = async (currentPostId) => {
    try {
      const response = await fetch('/api/posts', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.posts && Array.isArray(data.posts)) {
          // Posts are ordered by posted_at DESC (newest first)
          // Find the current post's index
          const currentIndex = data.posts.findIndex(p => p.id === currentPostId);
          
          if (currentIndex !== -1) {
            // Get the 3 posts immediately after the current post (older posts)
            // These are the posts that came before the current one chronologically
            const previous = data.posts.slice(currentIndex + 1, currentIndex + 4);
            setPreviousPosts(previous);
          } else {
            setPreviousPosts([]);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching previous posts:', err);
    }
  };
  if (loading) {
    return (
      <div style={{
        padding: '60px 0',
        textAlign: 'center'
      }}>
        <div className="spinner-border" role="status" style={{ color: '#667eea' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
        <p style={{
          marginTop: '1rem',
          color: '#718096',
          fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif'
        }}>
          Loading post...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '60px 0',
        textAlign: 'center'
      }}>
        <p style={{
          color: '#e53e3e',
          fontSize: '1rem',
          fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif'
        }}>
          {error}
        </p>
      </div>
    );
  }

  if (!post) {
    return (
      <div style={{
        padding: '60px 0',
        textAlign: 'center'
      }}>
        <p style={{
          color: '#718096',
          fontSize: '1rem',
          fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif'
        }}>
          Post not found
        </p>
      </div>
    );
  }

  const postedDate = formatDateTimeCT(post.posted_at);
  const wasUpdated = post.updated_at && post.posted_at && 
    new Date(post.updated_at) > new Date(post.posted_at);
  const updatedDate = wasUpdated ? formatDateTimeCT(post.updated_at) : null;

  return (
    <article>
      <QuillStyles />
      {/* Post Header */}
      <header style={{ marginBottom: '2rem' }}>
        <h1 style={{
          fontSize: '2.5rem',
          fontWeight: '300',
          color: '#2d3748',
          marginBottom: '1rem',
          fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
          lineHeight: '1.2'
        }}>
          {post.title}
        </h1>
        <div style={{
          fontSize: '0.875rem',
          color: '#718096',
          fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif'
        }}>
          <div>Posted: {postedDate}</div>
          {wasUpdated && updatedDate && (
            <div style={{ marginTop: '0.25rem' }}>
              Updated: {updatedDate}
            </div>
          )}
        </div>
        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div style={{ marginTop: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {post.tags.map((tag, index) => (
              <span
                key={index}
                onClick={() => {
                  window.location.href = `/ai-notes?tag=${encodeURIComponent(tag)}`;
                }}
                style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  backgroundColor: '#edf2f7',
                  color: '#4a5568',
                  borderRadius: '12px',
                  fontSize: '0.8rem',
                  fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#667eea';
                  e.target.style.color = '#fff';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#edf2f7';
                  e.target.style.color = '#4a5568';
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </header>

      {/* Post Content with Image */}
      <div 
        className="post-content-wrapper"
        style={{
          position: 'relative',
          marginBottom: '2rem'
        }}
      >
        {/* Post Image - Float on the right (desktop), full width on mobile */}
        {post.image && (post.image.webp_url || post.image.s3_url) && (
          <div
            className="post-image-container"
            style={{
              float: 'right',
              width: '50%',
              maxWidth: '600px',
              marginLeft: '2rem',
              marginBottom: '1rem',
              padding: '1px',
              borderRadius: '12px',
              backgroundColor: '#f7fafc',
              border: '1px solid #e2e8f0'
            }}
          >
            <img
              src={post.image.webp_url || post.image.s3_url}
              alt={post.image_alt_text || post.title}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                maxHeight: '400px',
                objectFit: 'contain',
                borderRadius: '8px'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}

        {/* Post Content - Text flows around image and wraps below */}
        <div
          className="post-content-text ql-content"
          style={{
            wordWrap: 'break-word',
            padding: 0
          }}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(post.content || '', {
              ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del', 'h2', 'h3', 'h4', 'blockquote', 'ul', 'ol', 'li', 'a', 'span'],
              ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
              FORBID_TAGS: ['script', 'style', 'iframe', 'form', 'input'],
              FORBID_ATTR: ['onclick', 'onerror', 'onload', 'onmouseover', 'onmouseout']
            })
          }}
        />
        
        {/* Clear float */}
        <div style={{ clear: 'both' }}></div>
      </div>

      {/* Newsletter Signup */}
      <NewsletterSignup />

      {/* Previous Notes Section */}
      {previousPosts.length > 0 && (
        <div style={{
          marginTop: '4rem',
          paddingTop: '3rem',
          borderTop: '1px solid #e2e8f0'
        }}>
          <h2 style={{
            fontSize: '1.75rem',
            fontWeight: '300',
            color: '#2d3748',
            marginBottom: '2rem',
            fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif'
          }}>
            Prior Posts
          </h2>
          <PostGrid posts={previousPosts} />
        </div>
      )}
      
      {/* Add responsive styles via style tag */}
      <style>{`
        @media (max-width: 768px) {
          .post-image-container {
            float: none !important;
            width: 100% !important;
            max-width: 100% !important;
            margin-left: 0 !important;
            margin-bottom: 1.5rem !important;
          }
        }
      `}</style>
    </article>
  );
};

export default PostDetail;

